import { normalize, schema } from 'normalizr'
import {
  IHotspot,
  IServiceCreateObject,
  IServiceFetchItemResponse,
  IServiceFetchItemsResponse,
  IServiceGroupRideRadiusUpdate,
  IServiceLocationObject,
  IServiceObject,
  IServicePricingTypeObject,
  IServiceUpdateObject,
  IWorkingHoursItem
} from '../types'
import { serverApi } from '../utils/request'

class ServiceEntityService {
  private urlBase = '/api/v1/services'
  private urlBase1 = '/api/v1/'
  private productsSchema = new schema.Entity('products')
  private providerSchema = new schema.Entity('providers', {
    providerProducts: [this.productsSchema]
  })
  private programsSchema = new schema.Entity('servicePrograms')
  private serviceSchema = new schema.Entity('services', {
    providers: [this.providerSchema],
    serviceProgram: this.programsSchema
  })

  public async fetchServices(
    type: string,
    orgId: string,
    orderBy?: string,
    sort?: string,
    take?: number,
    skip?: number
  ): Promise<IServiceFetchItemsResponse> {
    const orderByParam = orderBy !== 'undefined' ? `&orderBy=${orderBy}` : ''
    const sortParam = sort !== 'undefined' ? `&sort=${sort}` : ''
    const takeParam = typeof take === 'number' ? `&take=${take}` : ''
    const skipParam = typeof skip === 'number' ? `&skip=${skip}` : ''
    const result: any = await serverApi.get(
      `${this.urlBase}/doGetByOrgId/${orgId}?types=${type}${orderByParam}${sortParam}${takeParam}${skipParam}`
    )

    const normalized = normalize(result.data.items, [this.serviceSchema])
    const { services, servicePrograms } = normalized.entities
    return { services, servicePrograms, total: result.data.total }
  }

  public async fetchServiceMainInfo(serviceId: string): Promise<IServiceFetchItemResponse> {
    const result = await serverApi.get(`${this.urlBase}/${serviceId}`)

    const normalized = normalize(result.data, this.serviceSchema)
    const { services, products, servicePrograms } = normalized.entities
    if (products) {
      services[serviceId].products = Object.keys(products)
    }
    return { services, servicePrograms }
  }

  public async createService(service: IServiceCreateObject): Promise<IServiceObject> {
    const createdService = await serverApi.post<any>(this.urlBase, service)
    const { serviceProgram, ...newService } = createdService.data
    newService.serviceProgram = serviceProgram.id
    return newService
  }

  public async updateService(service: IServiceUpdateObject): Promise<IServiceObject> {
    const updatedService = await serverApi.put<any>(this.urlBase, service)

    const { serviceProgramId, organizationId, ...rest } = updatedService.data
    rest.serviceProgram = serviceProgramId
    rest.organization = { id: organizationId }
    return rest
  }

  public async updateServiceGroupRideRadius(service: IServiceGroupRideRadiusUpdate): Promise<IServiceObject> {
    const updatedService = await serverApi.patch<any>(`${this.urlBase}/groupRideInfo`, service)

    const { serviceProgramId, organizationId, ...rest } = updatedService.data
    rest.serviceProgram = serviceProgramId
    rest.organization = { id: organizationId }
    return rest
  }

  public updateServiceProducts(serviceId: string, serviceProductsIds: string[]) {
    return serverApi
      .post(`${this.urlBase}/doUpdateProviderProducts`, {
        serviceId,
        productsIds: serviceProductsIds
      })
      .then(
        response => response.data,
        error => {
          throw error.data
        }
      )
  }

  public async archive(serviceId: string): Promise<void> {
    const result = await serverApi.put<void>(`${this.urlBase}/doArchive/${serviceId}`)
    return result.data
  }

  public async restore(serviceId: string): Promise<void> {
    const result = await serverApi.put<void>(`${this.urlBase}/doRestoreArchive/${serviceId}`)
    return result.data
  }

  public async search(orgId: string, query: string, types: string): Promise<IServiceObject[]> {
    const result = await serverApi.get<IServiceObject[]>(`${this.urlBase}/doSearch/${orgId}/${query}?types=${types}`)
    return result.data
  }

  public async saveServiceDaysHours(
    serviceId: string,
    isAllDaysActive: boolean,
    serviceDays: IWorkingHoursItem[]
  ): Promise<any> {
    return serverApi
      .put(`${this.urlBase}/doUpdateOneWithServiceDays`, {
        serviceId,
        isAllDaysActive,
        serviceDays
      })
      .then(
        response => response.data,
        error => {
          throw error.data
        }
      )
  }

  public async getServiceByLocations(locations: any): Promise<any> {
    const result = await serverApi.post(`${this.urlBase}/getServicesByLocations`, {
      locations
    })
    const normalized = normalize(result.data, [this.serviceSchema])
    const { services, servicePrograms } = normalized.entities
    return { services, servicePrograms }
  }

  //PricingType Default URL
  public async fetchServicePricingType(serviceId: string): Promise<any> {
    const result = await serverApi.get<any>(
      `${this.urlBase1}servicePricingRiders/riderTypeFares?serviceId=${serviceId}`
    )
    return result
  }

  // DESIGNATED ZONE

  public async deleteServiceDesignatedZone(id: string): Promise<any> {
    const result = await serverApi.delete<any>(`${this.urlBase1}designatedZone/${id}`)
    return result
  }

  public async fetchServiceHotspots(serviceId: string): Promise<any> {
    const result = await serverApi.get<any>(`${this.urlBase1}designatedZone/${serviceId}`)
    return result
  }

  public async updateServiceHotspots(payload: { hotspots: IHotspot[] }): Promise<any> {
    const result = await serverApi.put<any>(`${this.urlBase1}designatedZone/hotspots`, payload)
    return result
  }
  public async deleteServiceDesignatedHotspot(id: string): Promise<any> {
    const result = await serverApi.delete<any>(`${this.urlBase1}designatedZone/hotspot/${id}`)
    return result
  }

  //PricingType Update Default URL
  public async UpdateAllServicePricingType(serviceId: string, dto: IServicePricingTypeObject): Promise<any> {
    const result = await serverApi.put<any>(`${this.urlBase1}servicePricingRiders/riderTypeFares/${serviceId}`, dto)
    return result
  }

  public async getServiceLocationList(serviceId: string): Promise<any> {
    const result = await serverApi.get<any>(`${this.urlBase1}serviceLocation/${serviceId}`)
    console.log(result)
    return result
  }

  public async updateServiceLocation(dto: IServiceLocationObject): Promise<any> {
    const result = await serverApi.put<any>(`${this.urlBase1}serviceLocation/doUpdateOne`, dto)
    return result
  }

  public async createServiceLocation(serviceId: string, dto: IServiceLocationObject): Promise<any> {
    const result = await serverApi.post<any>(`${this.urlBase1}serviceLocation/${serviceId}`, dto)
    return result
  }

  public async deleteServiceLocation(id: string): Promise<any> {
    const result = await serverApi.delete<any>(`${this.urlBase1}serviceLocation/doDeleteOne/${id}`)
    return result
  }
}

const serviceEntityService = new ServiceEntityService()
export { serviceEntityService }



// WEBPACK FOOTER //
// ./src/services/Service.ts